import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UIState {
  // Table preferences
  tableView: 'comfortable' | 'compact' | 'spacious';
  hiddenColumns: string[];
  columnOrder: string[];
  columnWidths: Record<string, number>;
  
  // Photo preferences
  photoView: 'grid' | 'comparison';
  photoGroupBy: 'property' | 'file' | 'none';
  imageTypeFilter: 'all' | 'aerial' | 'street_view';
  versionFilter: 'all' | 'marked' | 'unmarked';
  
  // Global preferences
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark';
  
  // Actions
  setTableView: (view: 'comfortable' | 'compact' | 'spacious') => void;
  toggleColumn: (columnKey: string) => void;
  setColumnOrder: (order: string[]) => void;
  setColumnWidth: (columnKey: string, width: number) => void;
  setPhotoView: (view: 'grid' | 'comparison') => void;
  setPhotoGroupBy: (groupBy: 'property' | 'file' | 'none') => void;
  setImageTypeFilter: (filter: 'all' | 'aerial' | 'street_view') => void;
  setVersionFilter: (filter: 'all' | 'marked' | 'unmarked') => void;
  toggleSidebar: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Defaults
      tableView: 'comfortable',
      hiddenColumns: [],
      columnOrder: [],
      columnWidths: {},
      photoView: 'grid',
      photoGroupBy: 'property',
      imageTypeFilter: 'all',
      versionFilter: 'all',
      sidebarCollapsed: false,
      theme: 'light',
      
      // Actions
      setTableView: (view) => set({ tableView: view }),
      
      toggleColumn: (columnKey) => set((state) => ({
        hiddenColumns: state.hiddenColumns.includes(columnKey)
          ? state.hiddenColumns.filter((c) => c !== columnKey)
          : [...state.hiddenColumns, columnKey],
      })),
      
      setColumnOrder: (order) => set({ columnOrder: order }),
      
      setColumnWidth: (columnKey, width) => set((state) => ({
        columnWidths: {
          ...state.columnWidths,
          [columnKey]: width,
        },
      })),
      
      setPhotoView: (view) => set({ photoView: view }),
      
      setPhotoGroupBy: (groupBy) => set({ photoGroupBy: groupBy }),
      
      setImageTypeFilter: (filter) => set({ imageTypeFilter: filter }),
      
      setVersionFilter: (filter) => set({ versionFilter: filter }),
      
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: 'ui-preferences',
    }
  )
);